import React, { useEffect, useRef, useState } from "react";
import { STATUS, PROVINCES } from "../../Constants";
import InputFormField from "../../components/InputFormField";
import SelectFormField from "../../components/SelectFormField";
import { updateEntity, deleteEntity, addEntity } from "../../API";
import UpdateDeleteBar from "../../components/UpdateDeleteBar";
import { Form, useLoaderData, useNavigate } from "react-router-dom";
import {
  assert,
  type,
  string,
  nonempty,
  StructError,
  optional,
  nullable,
  number,
  array,
} from "superstruct";
import { getEntities } from "../../API";
import Questions from "../../components/Questions";
import ItemsSelector from "../../components/ItemsSelector";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";

const taskSchema = type({
  id: optional(string()),
  name: nonempty(string()),
  status: nonempty(string()),
  startDate: nonempty(string()),
  endDate: nonempty(string()),
  manufacturer: nullable(optional(type({ id: number(), name: string() }))),
  provinces: nonempty(array(type({ id: number(), name: string() }))),
  territories: nonempty(array()),
  banners: nonempty(array()),
  stores: nonempty(array()),
  representatives: nonempty(array()),
  questions: nonempty(array()),
});

function hasErrors(task) {
  try {
    assert(task, taskSchema);
  } catch (err) {
    if (err instanceof StructError) {
      const fieldsErrors = err.failures().reduce(
        (acc, { key, message }) => ({
          ...acc,
          [key]: message,
        }),
        {},
      );
      return fieldsErrors;
    }
  }
  return null;
}

function Task() {
  const data = useLoaderData();

  const refForm = useRef();

  const navigate = useNavigate();

  useEffect(() => {
    handleProvinceSelection(task.provinces, false);
    handleTerritorySelection(task.territories, false);
    handleBannerSelection(task.banners, false);
  }, [data]);

  const itemDelete = async () => {
    await deleteEntity("tasks", task);
    navigate(`../tasks`);
  };

  const submit = () => {
    refForm.current?.dispatchEvent(
      new Event("submit", { cancelable: true, bubbles: true }),
    );
  };

  const itemSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const _task = Object.fromEntries(formData);

    _task.manufacturer = task.manufacturer
      ? { id: task.manufacturer.id, name: task.manufacturer.name }
      : null;
    _task.completionLimit = task.completionLimit ?? null;

    _task.provinces = task.provinces;
    _task.territories = task.territories;
    _task.banners = task.banners;
    _task.stores = task.stores.map((s) => {
      return { taskStoreKey: { storeId: s.id } };
    });
    _task.representatives = task.representatives.map((r) => {
      return { taskRepresentativeKey: { representativeId: r.id } };
    });
    _task.questions = questions;
    //validate
    const errs = hasErrors(_task);

    if (errs) {
      setErrors(errs);
      return;
    }

    _task.startDate = new Date(_task.startDate).getTime();
    _task.endDate = new Date(_task.endDate).getTime();

    setIsSaving(true);
    try {
      if (task.id) {
        _task.id = task.id;
        await updateEntity("tasks", _task);
      } else {
        await addEntity("tasks", _task);
      }
      navigate(`../tasks`);
    } finally {
      setIsSaving(false);
    }
  };

  const [questions, setQuestions] = useState(data.task.questions);

  const [task, setTask] = useState(data.task);

  const [errors, setErrors] = useState({});

  const [isSaving, setIsSaving] = useState(false);

  const [filteredTerritories, setFilteredTerritories] = useState([]);
  const [filteredRepresentatives, setFilteredRepresentatives] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);

  const updateErrors = (_task) => {
    if (errors && Object.keys(errors).length > 0) {
      const errs = hasErrors(_task);
      setErrors(errs);
    }
  };

  const handleTerritorySelection = (items, updateTask = true) => {
    /*const _representatives = data.representatives.filter((r) =>
      items.find((t) => r.territories.find((t2) => t2.id === t.id)),
    );

    const _banners = data.banners.filter((b) =>
      items.find((t) => b.provinces.find((p) => p.id === t.province.id)),
    );

    if (updateTask) {
      const keptRepresentatives = task.representatives.filter((r) =>
        items.find((t) => r.territories.find((t2) => t2.id === t.id)),
      );
      const keptBanners = task.banners.filter((b) =>
        items.find((t) => b.provinces.find((p) => p.id === t.province.id)),
      );
      setTask({
        ...task,
        territories: [...items],
        representatives: keptRepresentatives,
        banners: keptBanners,
      });
    }

    setFilteredRepresentatives(_representatives);
    setFilteredBanners(_banners);*/

    let _representatives = data.representatives.filter((r) => {
      return items.find((t) => r.territories.find((t2) => t2.id == t.id));
    });

    if (updateTask) {
      const _task = {
        ...task,
        territories: [...items],
        representatives: [],
        banners: [],
        stores: [],
      };
      setTask(_task);

      updateErrors(_task);
    }
    setFilteredRepresentatives(_representatives);

    /*const _banners = task.banners.filter((b) =>
      items.find((t) => b.provinces.find((p) => p.id === t.province.id)),
    );*/

    let _banners = data.banners.filter((b) => {
      return items.find((t) => b.provinces.find((p) => p.id == t.province.id));
    });

    setFilteredStores([]);
    setFilteredBanners(_banners);
  };

  const handleProvinceSelection = (provinces, updateTask = true) => {
    /*const _territories = data.territories.filter((t) => {
      return provinces.find((p) => p.id === t.province.id);
    });

    //Il faut ajuster les territoires en fct des provinces sélectionnées
    // const territories = task.territories.filter((t) => {
    //   return provinces.find((p) => p.id === t.province.id);
    // });

    setFilteredTerritories(_territories);

    if (updateTask)
      setTask({
        ...task,
        provinces: [...provinces],
      });

    handleTerritorySelection(_territories);*/

    const _territories = data.territories.filter((t) => {
      return provinces.find((p) => p.id == t.province.id);
    });

    setFilteredTerritories(_territories);

    if (updateTask) {
      const _task = {
        ...task,
        provinces: [...provinces],
        territories: [],
        representatives: [],
        banners: [],
        stores: [],
      };

      setTask(_task);

      updateErrors(_task);

      setFilteredRepresentatives([]);
      setFilteredBanners([]);
      setFilteredStores([]);
    }
  };

  const handleRepresentativeSelection = (representatives) => {
    //setTask({ ...task, representatives });
    const _task = { ...task, representatives };
    setTask(_task);
    updateErrors(_task);
  };

  const handleManufacturerSelection = (manufacturer) => {
    //setTask({ ...task, manufacturer });
    const _task = { ...task, manufacturer };
    setTask(_task);
    updateErrors(_task);
  };

  const handleBannerSelection = (items, updateTask = true) => {
    if (updateTask) {
      const _task = { ...task, banners: items, stores: [] };
      setTask(_task);
      updateErrors(_task);
    }

    if (items.length === 0) {
      setTask({ ...task, stores: [] });
      setFilteredStores([]);
      return;
    }

    const bannerIds = items.map((b) => `bannerIds=${b.id}`).join("&");
    const territoryIds = task.territories
      .map((t) => `territoryIds=${t.id}`)
      .join("&");
    const searchParams = `?${bannerIds}&${territoryIds}&size=1000`;

    getEntities("stores/by-banners-and-territories", searchParams).then(
      (response) => {
        const newFilteredStores = response?.content ?? [];
        setFilteredStores(newFilteredStores);
      },
    );
  };

  const addQuestion = (questionType) => {
    const question = {
      type: questionType.name,
      text: "",
      choices: [],
      no: questions.length + 1,
    };
    question.choices.push({
      no: question.choices.length + 1,
      text: "",
    });
    //Deux choix si CHECK ou RADIO
    if (questionType.name === "RADIO" || questionType.name === "CHECK")
      question.choices.push({
        no: question.choices.length + 1,
        text: "",
      });
    setQuestions([...questions, question]);
  };

  const updateQuestion = (q) => {
    const i = questions.findIndex((_q) => _q.no === q.no);

    questions.splice(i, 1, q);

    setQuestions([...questions]);
  };

  const deleteQuestion = (q) => {
    let _questions = questions.filter((_q) => _q.no !== q.no);

    _questions = _questions.map((q, i) => {
      return { ...q, no: i + 1 };
    });
    setQuestions(_questions);
  };

  return (
    <div className="flex-grow-1 overflow-auto">
      <UpdateDeleteBar onDelete={itemDelete} onSave={submit} title={task.name || "New Task"} isSaving={isSaving} />
      <div className="container-fluid py-4 px-4">
        <Form onSubmit={itemSave} ref={refForm} id="task-form">

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-8">
                  <InputFormField label="Name" name="name" value={task.name} error={errors?.name} />
                </div>
                <div className="col-md-4">
                  <SelectFormField
                    label="Status"
                    name="status"
                    value={task.status ? { name: task.status } : null}
                    error={errors?.status}
                    options={STATUS}
                    onChange={(item) => {
                      const _task = { ...task, status: item.name };
                      setTask(_task);
                      updateErrors(_task);
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <InputFormField label="Start Date" name="startDate" value={task.startDate} error={errors?.startDate} type="date" />
                </div>
                <div className="col-md-6">
                  <InputFormField label="End Date" name="endDate" value={task.endDate} error={errors?.endDate} type="date" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-medium mb-1">Provinces</label>
                  {errors?.provinces && <div className="form-error small mb-1">{errors.provinces}</div>}
                  <DropdownMultiSelect
                    options={PROVINCES}
                    value={task.provinces}
                    onChange={handleProvinceSelection}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    placeholder="Select provinces…"
                    className={errors?.provinces ? "is-invalid" : ""}
                  />
                </div>
                <div className="col-md-4">
                  <SelectFormField
                    label="Manufacturer"
                    name="manufacturer"
                    value={task.manufacturer}
                    error={errors?.manufacturer}
                    options={data.manufacturers}
                    onChange={handleManufacturerSelection}
                    objectValue={true}
                  />
                </div>
                <div className="col-md-2">
                  <SelectFormField
                    label="Completion Limit"
                    value={task.completionLimit != null ? { name: String(task.completionLimit) } : { name: "Unlimited" }}
                    options={[{ name: "1" }, { name: "2" }, { name: "3" }, { name: "Unlimited" }]}
                    onChange={(item) => {
                      setTask({ ...task, completionLimit: item.name === "Unlimited" ? null : parseInt(item.name) });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-2">
                <ItemsSelector
                  name="Territories"
                  values={task.territories}
                  items={filteredTerritories}
                  error={errors?.territories}
                  fields={["name"]}
                  enable={filteredTerritories.length !== 0}
                  onSave={handleTerritorySelection}
                />
                <ItemsSelector
                  name="Representatives"
                  values={task.representatives}
                  items={filteredRepresentatives}
                  error={errors?.representatives}
                  fields={["lastName", "firstName"]}
                  enable={filteredRepresentatives.length !== 0}
                  onSave={handleRepresentativeSelection}
                />
                <ItemsSelector
                  name="Banners"
                  values={task.banners}
                  items={filteredBanners}
                  error={errors?.banners}
                  fields={["name"]}
                  enable={filteredBanners.length !== 0}
                  onSave={handleBannerSelection}
                />
                <ItemsSelector
                  name="Stores"
                  values={task.stores}
                  items={filteredStores}
                  error={errors?.stores}
                  fields={["name", "number", "civicNo", "street", "city"]}
                  enable={filteredStores.length !== 0}
                  onSave={(items) => {
                    const _task = { ...task, stores: items };
                    setTask(_task);
                    updateErrors(_task);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              {errors?.questions && (
                <div className="text-danger small mb-2">You must add at least one question</div>
              )}
              <Questions
                questions={questions}
                onAddQuestion={addQuestion}
                onQuestionUpdate={updateQuestion}
                onDeleteQuestion={deleteQuestion}
              />
            </div>
          </div>

        </Form>
      </div>
    </div>
  );
}

export default Task;
